import { IRoute } from '../../interfaces';
import { isCustomerType } from '../../utils/userType';

export interface NavGroup {
  /** Overline shown above the group when the rail is expanded. Omitted for the top group. */
  label?: string;
  items: IRoute[];
}

/** Widths of the desktop rail in each pinned state, and of the phone sheet. */
export const NAV_WIDTH_EXPANDED = 256;
export const NAV_WIDTH_COLLAPSED = 72;
export const NAV_WIDTH_MOBILE = 304;

/** Routes that live in the account menu rather than the nav list. */
const ACCOUNT_ROUTE_KEYS = new Set(['settings-route']);

/**
 * How the admin's menu routes cluster. Anything with `menu: true` that is not
 * named here lands in the top group so a new route can never silently vanish.
 */
const ADMIN_GROUPS: { label?: string; keys: string[] }[] = [
  { keys: ['admin-dashboard'] },
  {
    label: 'Work',
    keys: ['customers-admin', 'listings-admin', 'transactions-admin', 'selling-route'],
  },
  { label: 'Queues', keys: ['tasks-admin', 'escalations-admin', 'review-queue-admin'] },
  {
    label: 'Tools',
    keys: ['browse-properties', 'cma-workspace-admin', 'comps-admin', 'pricing-calculator-admin', 'testimonials-admin'],
  },
];

/** Whether a menu route is offered to this account type. */
export const isRouteVisibleTo = (route: IRoute, userType?: string): boolean => {
  if (!route.menu) return false;
  if (ACCOUNT_ROUTE_KEYS.has(route.key)) return false;
  // PBUYERs get their own Home (/pbuyer); admins land on /admin "Today" instead.
  if (route.key === 'home' && (userType === 'PBUYER' || userType === 'ADMIN')) {
    return false;
  }
  if (route.sellerOnly) return isCustomerType(userType);
  if (route.adminOnly) return userType === 'ADMIN';
  return true;
};

/**
 * The nav list for an account: one flat group for customers, the labelled
 * Work / Queues / Tools clusters for admins. `extra` routes (the PBUYER home)
 * are prepended to the first group.
 */
export const buildNavGroups = (
  routes: IRoute[],
  userType?: string,
  extra: IRoute[] = [],
  hiddenKeys: ReadonlySet<string> = new Set()
): NavGroup[] => {
  const visible = routes.filter(
    (route) => isRouteVisibleTo(route, userType) && !hiddenKeys.has(route.key)
  );

  if (userType !== 'ADMIN') {
    return [{ items: [...extra, ...visible] }];
  }

  const placed = new Set<string>();
  const groups: NavGroup[] = ADMIN_GROUPS.map((group) => ({
    label: group.label,
    items: group.keys.flatMap((key) => {
      const route = visible.find((candidate) => candidate.key === key);
      if (!route) return [];
      placed.add(key);
      return [route];
    }),
  }));
  const unplaced = visible.filter((route) => !placed.has(route.key));
  groups[0].items = [...extra, ...groups[0].items, ...unplaced];

  return groups.filter((group) => group.items.length > 0);
};

/** Route titles the header should not echo verbatim. */
const HEADER_TITLE_OVERRIDES: Record<string, string> = {
  'pbuyer-dashboard-route': 'Home',
  'customers-admin': 'Client Pipeline',
  'pricing-calculator-admin': 'Install Pricing Calculator',
  'testimonials-admin': 'Video testimonials',
};

const hasParams = (route: IRoute): boolean => route.path.includes(':');

/**
 * The header title for a location. Static paths are tried before
 * parameterised ones so `/settings` resolves to Settings rather than to the
 * `/:listingSlug` catch-all that precedes it in the route table.
 */
export const headerTitleFor = (
  routes: IRoute[],
  pathname: string,
  match: (pattern: string, pathname: string) => unknown
): string => {
  const ordered = [...routes.filter((r) => !hasParams(r)), ...routes.filter(hasParams)];
  const route = ordered.find((candidate) => match(candidate.path, pathname));
  if (!route) return '';
  return HEADER_TITLE_OVERRIDES[route.key] ?? route.title;
};

/**
 * Whether a nav link must match its path exactly. `/admin` is a prefix of
 * every other admin page, so without this Today would light up alongside
 * Clients on /admin/customers. Links with no nested menu route keep prefix
 * matching, so Buying stays active on /buying/walkthrough/:showingId.
 */
export const requiresExactMatch = (route: IRoute, siblings: IRoute[]): boolean =>
  siblings.some(
    (other) => other.key !== route.key && other.path.startsWith(`${route.path}/`)
  );

/** "Caroline McMeans" → "CM"; falls back to the first letter of the email. */
export const initialsFor = (user?: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string => {
  const first = user?.firstName?.trim()?.[0];
  const last = user?.lastName?.trim()?.[0];
  const fromName = `${first ?? ''}${last ?? ''}`.toUpperCase();
  if (fromName) return fromName;
  return (user?.email?.trim()?.[0] ?? '?').toUpperCase();
};

export const roleLabelFor = (userType?: string): string => {
  if (userType === 'ADMIN') return 'Admin';
  if (userType === 'PBUYER') return 'Buyer';
  return 'Customer';
};

export interface NavActivity {
  /** Listings and offers have both been fetched; until then nothing is hidden. */
  loaded: boolean;
  sellerListings: number;
  buyerListings: number;
  offers: number;
}

export interface NavNarrowing {
  hiddenKeys: ReadonlySet<string>;
  /** Label for the account-menu link that reveals what is hidden; null when nothing is. */
  widenLabel: string | null;
}

const NOT_NARROWED: NavNarrowing = { hiddenKeys: new Set(), widenLabel: null };

/**
 * Which rail items a new customer does not need yet. A customer who picked
 * one side at signup and has nothing on the other side sees only their own:
 * a buyer loses Selling and, with no offers either, Offers; a seller loses
 * Buying. Both, or no stored intent, shows everything, as do admins and
 * PBUYERs. The hidden routes keep working by URL.
 */
export const narrowNavForIntent = (
  userType: string | undefined,
  intent: 'BUYER' | 'SELLER' | 'BOTH' | null,
  activity: NavActivity
): NavNarrowing => {
  if (!isCustomerType(userType) || !activity.loaded) return NOT_NARROWED;

  if (intent === 'BUYER' && activity.sellerListings === 0) {
    const hiddenKeys = new Set(['selling-route']);
    if (activity.offers === 0) hiddenKeys.add('offers-route');
    return { hiddenKeys, widenLabel: "I'm also selling" };
  }

  if (intent === 'SELLER' && activity.buyerListings === 0) {
    return { hiddenKeys: new Set(['buying-route']), widenLabel: "I'm also buying" };
  }

  return NOT_NARROWED;
};
